import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CategorySelector } from '@/components/category-selector';
import { CategoryTree } from '@/lib/graphql/generated/graphql';
import { useUpdateDealItems } from '@/lib/hooks/useUpdateDealItems';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

type CategoryProductSelectorProps = {
  dealId: string;
  dealItemId: string;
  productCategoryId: string | null;
  isEmpty: boolean;
  isOfferLocked: boolean;
  isParent?: boolean;
  displayValue?: string | null;
  onOpenChange?: (open: boolean) => void;
};

const CategoryProductSelector = ({
  dealId,
  dealItemId,
  productCategoryId,
  isEmpty,
  isOfferLocked,
  isParent = false,
  displayValue,
  onOpenChange,
}: CategoryProductSelectorProps) => {
  const { t } = useTranslation();
  const { updateProduct } = useUpdateDealItems(dealId);

  const handleCategoryChange = useCallback(
    (category: CategoryTree) => {
      if (isOfferLocked || isParent) return;
      updateProduct({
        productCategoryId: category.id,
        id: dealItemId,
      });
    },
    [dealItemId, updateProduct, isOfferLocked, isParent]
  );

  // For parent rows, show the aggregated value or error state
  if (isParent) {
    return (
      <div
        className={cn(
          'flex w-full items-center justify-between space-x-2 truncate rounded p-2',
          'cursor-pointer opacity-50'
        )}
      >
        {isEmpty ? (
          <span className="text-muted-foreground">
            {t('kpi.type_required')}
          </span>
        ) : (
          <span className="truncate">{displayValue || '—'}</span>
        )}
      </div>
    );
  }

  return (
    <CategorySelector
      value={productCategoryId}
      onValueChange={handleCategoryChange}
      placeholder={isEmpty ? t('kpi.type_required') : t('kpi.type_placeholder')}
      emptyContent={undefined}
      disabled={isOfferLocked}
      skipTooltipProvider={true}
      onOpenChange={onOpenChange}
    />
  );
};

/**
 * Props of the shared product-category cell. `categoryTree` is accepted (and
 * unused) so the table column can keep passing what it already resolves for
 * sorting without a separate shape.
 */
export type ProductCategoryCellProps = {
  isLoading: boolean;
  dealId: string;
  dealItemId: string;
  productCategoryId: string | null;
  isOfferLocked: boolean;
  isParent: boolean;
  displayValue?: string | null;
  isEmpty: boolean;
  categoryTree: CategoryTree[];
  isRecentlyModified: boolean;
};

const ProductCategoryCellContent: React.FC<ProductCategoryCellProps> = ({
  isLoading,
  dealId,
  dealItemId,
  productCategoryId,
  isOfferLocked,
  isParent,
  displayValue,
  isEmpty,
  isRecentlyModified,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  if (isLoading)
    return (
      <div className="flex w-full items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
      </div>
    );

  return (
    <div
      className={cn(
        'h-full w-full',
        isEmpty
          ? 'border-l-2 border-red-500 bg-red-50'
          : !isParent && isRecentlyModified
            ? 'border-l-2 border-blue-500 bg-blue-50'
            : '',
        isOpen && isEmpty
          ? 'border-red-500 ring-1 ring-inset ring-red-500'
          : isOpen
            ? 'border-black ring-1 ring-inset ring-black'
            : ''
      )}
    >
      <CategoryProductSelector
        dealId={dealId}
        dealItemId={dealItemId}
        productCategoryId={productCategoryId}
        isEmpty={isEmpty}
        isOfferLocked={isOfferLocked}
        isParent={isParent}
        displayValue={displayValue}
        onOpenChange={setIsOpen}
      />
    </div>
  );
};

/**
 * The product-category cell: the category selector plus its loading, empty and
 * recently-modified states. Shared so the main table column and the VAT review
 * dialog render the exact same control rather than two lookalikes.
 */
export const ProductCategoryCell = memo(ProductCategoryCellContent);
